import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import {
  BarChart3,
  Building2,
  CalendarDays,
  Check,
  Mail,
  MapPin,
  Phone,
  UserRound,
} from "lucide-react";
import {
  InstagramIcon,
  LineIcon,
  MessengerIcon,
  TelegramIcon,
  WhatsAppIcon,
} from "@/components/admin_dashboard/BrandIcons";

interface Props {
  step: number;
  businessName?: string;
}

const floatTransition = (delay = 0) => ({
  duration: 3.2,
  delay,
  repeat: Infinity,
  repeatType: "mirror" as const,
  ease: "easeInOut" as const,
});

const BusinessScene = ({ businessName }: { businessName?: string }) => (
  <div className="relative w-full max-w-md pb-28 pt-20">
    <motion.div
      animate={{ y: [0, -8, 0], rotate: [0, 1, 0] }}
      transition={floatTransition()}
      className="relative mx-auto w-full max-w-sm rounded-2xl border border-white/70 bg-white/90 p-5 shadow-xl shadow-primary/10 backdrop-blur"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">{businessName?.trim() || "Your business"}</p>
          <p className="text-xs text-muted-foreground">Business profile</p>
        </div>
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.35, type: "spring" }}
          className="ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white"
        >
          <Check className="h-3.5 w-3.5" />
        </motion.span>
      </div>
      <div className="mt-5 space-y-3">
        {["w-full", "w-4/5", "w-3/5"].map((width, index) => (
          <div key={width} className="h-2 overflow-hidden rounded-full bg-muted">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.25 + index * 0.15, duration: 0.65 }}
              className={`h-full origin-left rounded-full bg-primary/30 ${width}`}
            />
          </div>
        ))}
      </div>
    </motion.div>
    {[
      { Icon: UserRound, label: "Business name", position: "left-4 top-0" },
      { Icon: Mail, label: "Business email", position: "right-2 top-2" },
      { Icon: Phone, label: "Business number", position: "bottom-4 left-2" },
      { Icon: MapPin, label: "Business address", position: "bottom-4 right-0" },
    ].map(({ Icon, label, position }) => (
      <motion.div
        key={label}
        animate={{ y: [0, 8, 0], rotate: [-3, -1, -3] }}
        transition={floatTransition(0.4)}
        className={`absolute ${position} flex items-center gap-2 rounded-xl border border-white/70 bg-white px-3 py-2.5 shadow-lg`}
      >
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-xs font-medium text-foreground">{label}</span>
      </motion.div>
    ))}
  </div>
);

const CustomerImportScene = () => (
  <div className="w-full max-w-sm">
    <p className="mb-2 pl-1 text-xs font-medium text-foreground">Customers</p>
    <div className="overflow-hidden rounded-2xl border border-white/70 bg-white/90 shadow-xl shadow-primary/10 backdrop-blur">
      <div className="grid grid-cols-[1.35fr_1fr_0.8fr] gap-4 border-b border-border/70 bg-muted/40 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>Name</span>
        <span>Email</span>
        <span>Last active</span>
      </div>
      <div className="divide-y divide-border/60">
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <motion.div
            key={row}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: [0, 1, 1, 0], y: [10, 0, 0, -4] }}
            transition={{
              duration: 4.2,
              delay: row * 0.48,
              repeat: Infinity,
              repeatDelay: 0.3,
              times: [0, 0.12, 0.86, 1],
              ease: "easeInOut",
            }}
            className="grid grid-cols-[1.35fr_1fr_0.8fr] items-center gap-4 px-4 py-3.5"
          >
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 shrink-0 rounded-full bg-primary/10" />
              <div className={`h-2.5 rounded-full bg-primary/20 ${row % 2 === 0 ? "w-20" : "w-16"}`} />
            </div>
            <div className={`h-2.5 rounded-full bg-muted ${row % 3 === 0 ? "w-20" : "w-16"}`} />
            <div className={`h-2.5 rounded-full bg-muted ${row % 2 === 0 ? "w-14" : "w-10"}`} />
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);

const carouselApps = [
  { label: "WhatsApp", Icon: WhatsAppIcon },
  { label: "Telegram", Icon: TelegramIcon },
  { label: "Instagram", Icon: InstagramIcon },
  { label: "Messenger", Icon: MessengerIcon },
  { label: "LINE", Icon: LineIcon },
  { label: "Dibs Bookings", Icon: CalendarDays },
] as const;

const carouselRows = [
  carouselApps,
  [carouselApps[3], carouselApps[5], carouselApps[1], carouselApps[4], carouselApps[0], carouselApps[2]],
  [carouselApps[2], carouselApps[0], carouselApps[4], carouselApps[1], carouselApps[5], carouselApps[3]],
] as const;

const ConnectorsScene = () => (
  <div className="w-[620px] max-w-none -rotate-[6deg] space-y-5 py-10">
    {carouselRows.map((apps, rowIndex) => {
      const movesRight = rowIndex !== 1;
      return (
        <div
          key={rowIndex}
          className="overflow-hidden py-2 [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)]"
        >
          <motion.div
            className="flex w-max"
            animate={{ x: movesRight ? ["-33.333%", "0%"] : ["0%", "-33.333%"] }}
            transition={{
              duration: 22 + rowIndex * 2,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            {[0, 1, 2].map((copy) => (
              <div key={copy} className="flex gap-5 pr-5" aria-hidden={copy > 0}>
                {apps.map(({ label, Icon }, appIndex) => (
                  <div
                    key={`${copy}-${label}-${appIndex}`}
                    aria-label={copy === 0 ? label : undefined}
                    className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-2xl border border-white/80 bg-white/95 shadow-xl shadow-primary/10 backdrop-blur"
                  >
                    <Icon size={28} className={label === "Dibs Bookings" ? "text-primary" : undefined} />
                  </div>
                ))}
              </div>
            ))}
          </motion.div>
        </div>
      );
    })}
  </div>
);

const MetricsScene = () => (
  <div className="w-full max-w-sm rounded-2xl border border-white/70 bg-white/90 p-5 shadow-xl">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs text-muted-foreground">Revenue recovered</p>
        <p className="mt-1 text-2xl font-bold text-foreground">$8,420</p>
      </div>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
        <BarChart3 className="h-5 w-5" />
      </span>
    </div>
    <div className="mt-7 flex h-32 items-end gap-3">
      {[42, 64, 50, 82, 70, 100].map((height, index) => (
        <motion.div
          key={index}
          initial={{ height: 0 }}
          animate={{ height: `${height}%` }}
          transition={{ delay: index * 0.12, duration: 0.55, ease: "easeOut" }}
          className="flex-1 rounded-t-md bg-primary"
          style={{ opacity: 0.25 + index * 0.13 }}
        />
      ))}
    </div>
  </div>
);

const OnboardingVisual = ({ step, businessName }: Props) => {
  return (
    <MotionConfig reducedMotion="user">
      <aside className="relative hidden h-full min-h-0 overflow-hidden border-l border-primary/10 bg-gradient-to-br from-primary/[0.04] via-primary/[0.09] to-primary/[0.18] p-8 lg:flex lg:flex-col">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-white/60 blur-3xl" />
        <div className="relative z-10 flex flex-1 items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.35 }}
              className="flex w-full justify-center"
            >
              {step === 1 && <BusinessScene businessName={businessName} />}
              {step === 2 && <CustomerImportScene />}
              {step === 3 && <ConnectorsScene />}
              {step === 4 && <MetricsScene />}
            </motion.div>
          </AnimatePresence>
        </div>
      </aside>
    </MotionConfig>
  );
};

export default OnboardingVisual;
